import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import {
  materialFormToApi,
  materialMasterToForm,
} from 'src/app/shared/utils/material-master.util';

@Component({
  selector: 'app-add-construction-materials',
  templateUrl: './add-construction-materials.component.html',
  styleUrl: './add-construction-materials.component.css',
})
export class AddConstructionMaterialsComponent implements OnInit, OnDestroy {
  materialsForm!: FormGroup;
  constructionmaterialsId: string | null = null;
  viewMode = false;
  pageTitleKey = 'ADD_MATERIAL';
  organizationName = '';
  organizationCode = '';
  loadedName = '';
  loadedCode = '';
  saving = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private readonly apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly toastrMsgService: ToastrMsgService
  ) {}

  ngOnInit(): void {
    this.viewMode = this.route.snapshot.data['mode'] === 'view';
    this.pageTitleKey = this.viewMode ? 'VIEW_MATERIAL' : 'ADD_MATERIAL';

    const user = localStorage.getItem('user') as string | null;
    const userParse = user ? JSON.parse(user) : { user: {} };
    this.organizationName = userParse.user?.org_name ?? '';
    this.organizationCode = String(userParse.user?.company_code ?? userParse.user?.org_id ?? '');

    this.materialsForm = this.formBuilder.group({
      name: ['', Validators.required],
      category: ['', Validators.required],
      subcategory: [''],
      manufacturer_brand: [''],
      system_code: [''],
      region: [''],
      strength: [''],
      density: [''],
      size: [''],
      length: [null],
      width: [null],
      thickness: [null],
      unit: [''],
      quantity: [null],
      unit_cost: [null],
      total_cost: [{ value: null, disabled: true }],
      location: [''],
      leadTime: [null],
      carbon_footprint: [null],
      recyclability: [null],
      building_type: [''],
      use_case: [''],
      certifications: [''],
      testing_method: [''],
      tools: [''],
      method: [''],
      countries: [''],
      regions: [''],
      durability: [''],
      fire_resistance: [''],
      brand: [''],
      model: [''],
    });

    this.materialsForm.get('quantity')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncTotalCost());
    this.materialsForm.get('unit_cost')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncTotalCost());

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.constructionmaterialsId = params['id'] ?? null;
      if (this.constructionmaterialsId) {
        this.pageTitleKey = this.viewMode ? 'VIEW_MATERIAL' : 'EDIT_MATERIAL';
        this.loadMaterial(this.constructionmaterialsId);
      }
    });

    if (this.viewMode) {
      this.materialsForm.disable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageSubtitle(): string {
    if (this.loadedName || this.loadedCode) {
      return [this.loadedCode, this.loadedName].filter(Boolean).join(' — ');
    }
    if (this.organizationName) {
      return this.organizationName;
    }
    return '';
  }

  goToEdit(): void {
    if (!this.constructionmaterialsId) return;
    void this.router.navigate(['/home/edit-material', this.constructionmaterialsId]);
  }

  onSubmit(): void {
    if (this.viewMode) {
      return;
    }

    if (this.materialsForm.invalid) {
      this.materialsForm.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields.');
      return;
    }

    this.syncTotalCost();
    const payload = materialFormToApi(this.materialsForm.getRawValue());
    this.saving = true;

    if (this.constructionmaterialsId) {
      this.apiService.updateconstructionmaterials(this.constructionmaterialsId, payload).subscribe({
        next: () => {
          this.saving = false;
          this.toastrMsgService.success('Material updated successfully');
          void this.router.navigate(['/home/material-list']);
        },
        error: (err) => {
          this.saving = false;
          this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.error ?? 'Update failed');
        },
      });
      return;
    }

    this.apiService.saveconstructionmaterials(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success('Material added successfully');
        void this.router.navigate(['/home/material-list']);
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.error ?? 'Save failed');
      },
    });
  }

  loadMaterial(id: string): void {
    this.apiService.getConstructionMaterialById(id).subscribe({
      next: (res: any) => {
        const record = (res?.data as Record<string, unknown>) ?? (res as Record<string, unknown>);
        this.applyMaterialRecord(record);
      },
      error: () => {
        this.loadMaterialFromList(id);
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/home/material-list']);
  }

  private loadMaterialFromList(id: string): void {
    this.apiService.getconstructionmaterials().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res?.data ?? [];
        const data = list.find((material: any) => String(material.id) === String(id));
        if (!data) {
          this.toastrMsgService.error('Material not found');
          return;
        }
        this.applyMaterialRecord(data as Record<string, unknown>);
      },
      error: (err) => {
        this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.error ?? 'Failed to load material');
      },
    });
  }

  private applyMaterialRecord(record: Record<string, unknown>): void {
    const formValue = materialMasterToForm(record);
    this.materialsForm.patchValue(formValue, { emitEvent: false });
    this.loadedName = String(formValue['name'] ?? record['name'] ?? '');
    this.loadedCode = String(formValue['system_code'] ?? record['system_code'] ?? '');
    this.syncTotalCost();
    if (this.viewMode) {
      this.materialsForm.disable({ emitEvent: false });
    }
  }

  private syncTotalCost(): void {
    const qtyRaw = this.materialsForm.get('quantity')?.value;
    const rateRaw = this.materialsForm.get('unit_cost')?.value;
    const qty = Number(qtyRaw);
    const rate = Number(rateRaw);
    const hasQty = qtyRaw !== null && qtyRaw !== undefined && qtyRaw !== '';
    const hasRate = rateRaw !== null && rateRaw !== undefined && rateRaw !== '';
    const total =
      hasQty && hasRate && Number.isFinite(qty) && Number.isFinite(rate)
        ? Math.round(qty * rate * 100) / 100
        : null;
    this.materialsForm.get('total_cost')?.setValue(total, { emitEvent: false });
  }
}
